import React, { useEffect, useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  getTeamManagers,
  getSalesUsers,
  getSalesByTM,
  assignLeadsToTM,
  assignSalesLeads,
  reassignLeadsToUser,
} from "../../services/leadService";

import "./AssignLeadModal.css";

const AssignLeadModal = ({ leadIds = [], mode = "assign", onClose, onSuccess }) => {
  const [tms, setTms] = useState([]);
  const [sales, setSales] = useState([]);

  const [activeTab, setActiveTab] = useState("tm");
  const [selectedTM, setSelectedTM] = useState(null);
  const [selectedSales, setSelectedSales] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    initLoad();
  }, []);

  const initLoad = async () => {
    await Promise.all([loadTMs(), loadAllSales()]);
  };

  const loadTMs = async () => {
    const res = await getTeamManagers();
    setTms(Array.isArray(res?.data) ? res.data : []);
  };

  const loadAllSales = async () => {
    const res = await getSalesUsers();
    setSales(Array.isArray(res?.data) ? res.data : []);
  };

  const loadSalesByTM = async (tmId) => {
    const res = await getSalesByTM(tmId);
    return Array.isArray(res?.data?.sales) ? res.data.sales : [];
  };

  /* ================= TM SELECT ================= */
  const handleSelectTM = async (tmId) => {
    setSelectedTM(tmId);
    setSelectedSales([]);

    if (!tmId) return;

    // 1️⃣ Fetch sales
    const tmSales = await loadSalesByTM(tmId);

    // 2️⃣ Update state
    setSales(tmSales);

    // 3️⃣ Switch tab AFTER state update
    requestAnimationFrame(() => {
      setActiveTab("sales");
    });
  };

  /* ================= TAB CHANGE ================= */
  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setSearch("");

    if (tab === "sales") {
      if (selectedTM) {
        const tmSales = await loadSalesByTM(selectedTM);
        setSales(tmSales);
      } else {
        await loadAllSales();
      }
    }
  };

  /* ================= SALES SELECT ================= */
  const toggleSales = (id) => {
    setSelectedSales((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  /* ================= ASSIGN ================= */
  const handleAssign = async () => {
    if (!selectedTM && selectedSales.length === 0) {
      toast.error("Select Team Manager or Sales Executive");
      return;
    }

    setLoading(true);

    try {
      if (mode === "reassign") {
        if (selectedTM) {
          const res = await reassignLeadsToUser({
            user_id: selectedTM,
            role: "tm",
            lead_ids: leadIds,
          });
          if (res.status === "success") {
            toast.success(res.message || "Reassigned to Team Manager");
          } else {
            toast.error(res.message || "Reassignment failed");
          }
          if (res.data?.errors?.length) {
            res.data.errors.forEach((err) => {
              toast.error(`Lead ${err.lead_id}: ${err.error}`);
            });
          }
        }

        for (const sid of selectedSales) {
          const res = await reassignLeadsToUser({
            user_id: sid,
            role: "sales",
            lead_ids: leadIds,
          });
          if (res.status === "success") {
            toast.success(res.message || "Reassigned to Sales Executive");
          } else {
            toast.error(res.message || "Reassignment failed");
          }
          if (res.data?.errors?.length) {
            res.data.errors.forEach((err) => {
              toast.error(`Lead ${err.lead_id}: ${err.error}`);
            });
          }
        }
      } else {
        if (selectedTM) {
          await assignLeadsToTM({
            tm_id: selectedTM,
            lead_ids: leadIds,
          });
          toast.success("Assigned to Team Manager");
        }

        for (const sid of selectedSales) {
          await assignSalesLeads({
            sales_id: sid,
            lead_ids: leadIds,
          });
          toast.success("Assigned to Sales Executive");
        }
      }

      onSuccess && onSuccess();
      onClose();
    } catch {
      toast.error(mode === "reassign" ? "Reassignment failed" : "Assignment failed");
    }

    setLoading(false);
  };

  /* ================= FILTER ================= */
  const list = activeTab === "tm" ? tms : sales;

  const filteredList = list.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  /* ================= UI ================= */
  return (
    <div className="assign-overlay">
      <div className="assign-modal">
        <div className="assign-header">
          <h3>{mode === "reassign" ? "Reassign Leads" : "Assign Leads"}</h3>
          <button onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* ===== TABS ===== */}
        <div className="member-tabs">
          <button
            className={activeTab === "tm" ? "active" : ""}
            onClick={() => handleTabChange("tm")}
          >
            Team Manager
          </button>
          <button
            className={activeTab === "sales" ? "active" : ""}
            onClick={() => handleTabChange("sales")}
          >
            Sales Executive
          </button>
        </div>

        <input
          className="member-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
        />

        <div className="member-list">
          {filteredList.length === 0 && (
            <p className="empty-text">
              {activeTab === "sales"
                ? "No Sales Executives Found"
                : "No Team Managers Found"}
            </p>
          )}

          {filteredList.map((u) => {
            const selected =
              activeTab === "tm"
                ? u.id === selectedTM
                : selectedSales.includes(u.id);

            return (
              <div
                key={u.id}
                className={`member-item ${selected ? "selected" : ""}`}
                onClick={() =>
                  activeTab === "tm"
                    ? handleSelectTM(u.id)
                    : toggleSales(u.id)
                }
              >
                <span>{u.name}</span>
                {selected && <FaCheck />}
              </div>
            );
          })}
        </div>

        <div className="assign-actions">
          <button
            className="btn-primary"
            onClick={handleAssign}
            disabled={loading}
          >
            {loading ? (mode === "reassign" ? "Reassigning..." : "Assigning...") : (mode === "reassign" ? "Reassign" : "Assign")}
          </button>
          <button className="btn-light" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignLeadModal;
